import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { api } from '../api';
import type { TokenUsage } from '../types';

type Period = 'day' | 'week' | 'month';

export default function TokensPage() {
  const [period, setPeriod] = useState<Period>('month');
  const [data, setData] = useState<TokenUsage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    api<TokenUsage>(`/admin/tokens/usage?period=${period}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [period]);

  const dailyAvg = data?.daily_breakdown.length
    ? Math.round(data.daily_breakdown.reduce((s, d) => s + d.tokens, 0) / data.daily_breakdown.length)
    : 0;

  const featureEntries = data
    ? Object.entries(data.by_feature).sort(([, a], [, b]) => b - a)
    : [];
  const maxFeature = featureEntries[0]?.[1] || 1;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 20 }}>
        <div className="overline">Analytics</div>
        <h1 className="page-title">Token Usage</h1>
      </div>

      <div className="period-tabs">
        {(['day', 'week', 'month'] as Period[]).map(p => (
          <button
            key={p}
            className={`period-tab${period === p ? ' active' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : data && (
        <>
          <div className="kpi-grid kpi-grid-4" style={{ marginBottom: 24 }}>
            <div className="kpi-cell">
              <div className="kpi-label">Total Tokens</div>
              <div className="kpi-value">{data.total_tokens.toLocaleString()}</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">Est. Cost</div>
              <div className="kpi-value alert">${data.total_cost_usd.toFixed(4)}</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">Top User</div>
              <div className="kpi-value">{data.top_users[0]?.tokens.toLocaleString() ?? '—'}</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">Daily Average</div>
              <div className="kpi-value">{dailyAvg ? dailyAvg.toLocaleString() : '—'}</div>
            </div>
          </div>

          {/* ── Daily stacked chart ─────────────────────────────── */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-title">Daily Token Usage</div>
            <div className="legend">
              <span className="legend-label">
                <span className="legend-dot" style={{ background: 'var(--accent)' }} />
                Input tokens
              </span>
              <span className="legend-label">
                <span className="legend-dot" style={{ background: 'var(--rising)' }} />
                Output tokens
              </span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.daily_breakdown} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-s)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'var(--tx3)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 10, fill: 'var(--tx3)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', boxShadow: 'none' }}
                  cursor={{ fill: 'var(--bg3)' }}
                />
                <Bar dataKey="tokens_in" name="Input" stackId="a" fill="var(--accent)" />
                <Bar dataKey="tokens_out" name="Output" stackId="a" fill="var(--rising)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid-2" style={{ marginBottom: 24 }}>
            {/* ── Top users ───────────────────────────────────────── */}
            <div className="card">
              <div className="card-title">Top 10 Users by Consumption</div>
              {data.top_users.length === 0 ? (
                <div className="empty-state">No token data for this period</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>#</th>
                      <th>Email</th>
                      <th>Tokens</th>
                      <th>Cost</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_users.map((u, i) => (
                      <tr key={u.user_id}>
                        <td style={{ color: 'var(--tx3)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                        <td style={{ fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{u.tokens.toLocaleString()}</td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>${u.cost.toFixed(4)}</td>
                        <td>
                          <div className="mini-bar-wrap">
                            <div className="mini-bar-track">
                              <div
                                className="mini-bar-fill"
                                style={{
                                  width: data.total_tokens
                                    ? `${Math.round((u.tokens / data.total_tokens) * 100)}%`
                                    : '0%',
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 12, color: 'var(--tx2)', minWidth: 36 }}>
                              {data.total_tokens ? ((u.tokens / data.total_tokens) * 100).toFixed(1) : 0}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── By feature ──────────────────────────────────────── */}
            <div className="card">
              <div className="card-title">Usage by Feature</div>
              {featureEntries.length === 0 ? (
                <div className="empty-state">No feature data for this period</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Tokens</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureEntries.map(([feature, count]) => (
                      <tr key={feature}>
                        <td>
                          <span className="tag" style={{ textTransform: 'none' }}>
                            {feature.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {count.toLocaleString()}
                        </td>
                        <td>
                          <div className="feature-bar">
                            <div className="feature-bar-track">
                              <div
                                className="feature-bar-fill"
                                style={{ width: `${Math.round((count / maxFeature) * 100)}%` }}
                              />
                            </div>
                            <span style={{ fontSize: 12, color: 'var(--tx2)' }}>
                              {((count / maxFeature) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
